import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import useProjectStore from '../../store/projectStore';
import useUIStore from '../../store/uiStore';
import { getEmployees } from '../../services/employeeService';
import * as expenseService from '../../services/expenseService';
import ExpenseTabs from '../../components/ExpenseTabs';
import { downloadBlob } from '../../utils/downloadFile';
import ScrollX from '../../components/ScrollX';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Mirrors expense_routes.py's SITE_MANAGER_ROLES / ADMIN_ROLES, broadened
// 2026-08-19 so HOD/CEO/Accounts_Manager/System_Admin can all approve -
// UI convenience only, real enforcement is server-side.
const SITE_MANAGER_ROLES = ['Supervisor', 'Site_Lead', 'HOD', 'Accounts_Manager', 'System_Admin', 'CEO'];
const ADMIN_ROLES = ['HOD', 'Accounts_Manager', 'System_Admin', 'CEO'];

const CATEGORIES = ['Travel', 'Food', 'Accommodation', 'Materials', 'Transport', 'Other'];

const APPROVAL_LABELS = {
  pending: 'Pending',
  site_manager_approved: 'Site Manager Approved',
  approved: 'Approved',
  rejected: 'Rejected',
};

const APPROVAL_COLORS = {
  pending: 'bg-gray-200 text-gray-800',
  site_manager_approved: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const initialAdvanceForm = { engineer_id: '', project_id: '', amount: '', given_date: '', notes: '' };

// The "Log an expense" form is multi-line (2026-08-19 user request): pick
// the engineer/project once as a header, then add as many expense rows as
// needed and submit them together via /site/expenses/batch. The header
// stays populated after a successful submit ("sticky") so logging the
// next batch for the same engineer doesn't mean re-picking them.
const initialExpenseHeader = { engineer_id: '', project_id: '' };
// 2026-08-19 user instruction: an expense paid from advance draws from
// the engineer's overall advance pool, not one specific advance record -
// no advance_id to pick anymore (see expense_routes.py's matching
// comment). The pool balance itself is shown live from `settlements`.
const blankExpenseLine = () => ({
  key: Math.random().toString(36).slice(2), category: 'Travel', amount: '',
  expense_date: '', description: '', paid_from: 'advance',
});

const SiteExpensesPage = () => {
  const { user } = useAuthStore();
  const { projects, fetchProjects } = useProjectStore();
  const showToast = useUIStore((s) => s.showToast);
  const location = useLocation();
  const navigate = useNavigate();

  // Drill-down target from the dashboard's "Expenses by Project" chart
  // (?project_id=..) - seeds the real filter bar below rather than a
  // separate client-side filter, so it scales the same way as picking a
  // project by hand.
  const urlProjectId = useMemo(() => {
    const p = new URLSearchParams(location.search).get('project_id');
    return p ? Number(p) : null;
  }, [location.search]);

  const initialFilters = { engineer_id: '', project_id: '', category: '', date_from: '', date_to: '', approval_status: '' };
  const [filters, setFilters] = useState(() => (urlProjectId ? { ...initialFilters, project_id: String(urlProjectId) } : initialFilters));

  const [employees, setEmployees] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAdvanceForm, setShowAdvanceForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [advanceForm, setAdvanceForm] = useState(initialAdvanceForm);
  const [expenseHeader, setExpenseHeader] = useState(initialExpenseHeader);
  const [expenseLines, setExpenseLines] = useState([blankExpenseLine()]);
  const [isSubmittingExpenses, setIsSubmittingExpenses] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // 2026-08-19 user instruction: "everyone can log in all money, HOD/
  // accounts/admin/CEO approve it" - logging (giving/editing/deleting an
  // advance) is open to anyone logged in; only approval stays role-gated.
  // Mirrors expense_routes.py's create/update/delete_site_advance now
  // using get_current_employee instead of require_role.
  const canGiveAdvance = !!user;
  const canApproveSiteManager = user && SITE_MANAGER_ROLES.includes(user.role);
  const canApproveAdmin = user && ADMIN_ROLES.includes(user.role);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const advanceParams = {};
      if (filters.engineer_id) advanceParams.engineer_id = filters.engineer_id;
      if (filters.project_id) advanceParams.project_id = filters.project_id;

      const expenseParams = { ...advanceParams };
      if (filters.category) expenseParams.category = filters.category;
      if (filters.date_from) expenseParams.date_from = filters.date_from;
      if (filters.date_to) expenseParams.date_to = filters.date_to;
      if (filters.approval_status) expenseParams.approval_status = filters.approval_status;

      const [advRes, expRes, setRes] = await Promise.all([
        expenseService.getSiteAdvances(advanceParams),
        expenseService.getSiteExpenses(expenseParams),
        expenseService.getSiteSettlements(),
      ]);
      setAdvances(advRes.data.advances || []);
      setExpenses(expRes.data.expenses || []);
      setSettlements(setRes.data.settlements || []);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load site expenses', 'error');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    fetchProjects();
    getEmployees().then((res) => setEmployees(res.data.employees || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFilters = () => {
    setFilters(initialFilters);
    navigate('/expenses/site');
  };

  const nameOf = (list, id) => list.find((x) => x.id === Number(id))?.name || '—';

  const handleAdvanceSubmit = async (e) => {
    e.preventDefault();
    if (!advanceForm.engineer_id || !advanceForm.project_id || !advanceForm.amount) {
      showToast('Engineer, project, and amount are required', 'error');
      return;
    }
    try {
      await expenseService.createSiteAdvance({
        engineer_id: Number(advanceForm.engineer_id),
        project_id: Number(advanceForm.project_id),
        amount: Number(advanceForm.amount),
        given_date: advanceForm.given_date || null,
        notes: advanceForm.notes || null,
      });
      showToast('Advance recorded', 'success');
      setAdvanceForm(initialAdvanceForm);
      setShowAdvanceForm(false);
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to record advance', 'error');
    }
  };

  const updateExpenseLine = (index, field, value) => {
    setExpenseLines((lines) => lines.map((line, i) => (i === index ? { ...line, [field]: value } : line)));
  };

  const addExpenseLine = () => setExpenseLines((lines) => [...lines, blankExpenseLine()]);

  const removeExpenseLine = (index) => {
    setExpenseLines((lines) => (lines.length > 1 ? lines.filter((_, i) => i !== index) : lines));
  };

  const handleExpenseBatchSubmit = async (e) => {
    e.preventDefault();
    if (!expenseHeader.engineer_id || !expenseHeader.project_id) {
      showToast('Engineer and project are required', 'error');
      return;
    }
    for (let i = 0; i < expenseLines.length; i++) {
      const line = expenseLines[i];
      if (!line.amount || !line.expense_date) {
        showToast(`Line ${i + 1}: amount and date are required`, 'error');
        return;
      }
    }
    setIsSubmittingExpenses(true);
    try {
      await expenseService.createSiteExpensesBatch({
        engineer_id: Number(expenseHeader.engineer_id),
        project_id: Number(expenseHeader.project_id),
        expenses: expenseLines.map((line) => ({
          category: line.category,
          amount: Number(line.amount),
          expense_date: line.expense_date,
          description: line.description || null,
          paid_from: line.paid_from,
        })),
      });
      showToast(`${expenseLines.length} expense${expenseLines.length !== 1 ? 's' : ''} submitted`, 'success');
      // Sticky: keep the engineer/project header, just reset the lines -
      // logging the next batch for the same person doesn't mean re-picking them.
      setExpenseLines([blankExpenseLine()]);
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to submit expenses', 'error');
    } finally {
      setIsSubmittingExpenses(false);
    }
  };

  // Excel template with real dropdowns (engineer/project/category/paid_from,
  // all sourced live from the DB) - a plain CSV can't restrict a cell to a
  // dropdown, so "person should only select data" needs a real .xlsx (see
  // expense_routes.py's "XLSX bulk-import templates" section). All name
  // resolution and validation happens server-side; the frontend just moves
  // the file.
  const handleDownloadExpenseTemplate = async () => {
    try {
      const res = await expenseService.getSiteExpensesImportTemplate();
      downloadBlob(res.data, 'site_expenses_template.xlsx');
    } catch (error) {
      showToast('Failed to download template', 'error');
    }
  };

  const handleImportExpensesCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file next time
    if (!file) return;
    setIsImporting(true);
    try {
      const res = await expenseService.importSiteExpenses(file);
      const count = res.data.expenses.length;
      showToast(`${count} expense${count !== 1 ? 's' : ''} imported`, 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to import file', 'error');
    } finally {
      setIsImporting(false);
    }
  };

  const handleApproveSiteManager = async (id) => {
    try {
      await expenseService.approveSiteExpenseSiteManager(id);
      showToast('Approved (1st tier)', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Approval failed', 'error');
    }
  };

  const handleApproveAdmin = async (id) => {
    try {
      await expenseService.approveSiteExpenseAdmin(id);
      showToast('Approved (final)', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Approval failed', 'error');
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason) return;
    try {
      await expenseService.rejectSiteExpense(id, reason);
      showToast('Rejected', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Rejection failed', 'error');
    }
  };

  const handleMarkSettled = async (engineerId, engineerName) => {
    if (!window.confirm(`Mark all active advances for ${engineerName || 'this engineer'} as settled?`)) return;
    try {
      await expenseService.markSiteSettlementSettled(engineerId);
      showToast('Marked settled', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to mark settled', 'error');
    }
  };

  const handleEditAdvance = async (advance) => {
    const amount = window.prompt('New amount (₹):', advance.amount);
    if (amount === null) return;
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      showToast('Enter a valid amount', 'error');
      return;
    }
    try {
      await expenseService.updateSiteAdvance(advance.id, { amount: Number(amount) });
      showToast('Advance updated', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update advance', 'error');
    }
  };

  const handleDeleteAdvance = async (advance) => {
    if (!window.confirm('Delete this advance? This cannot be undone.')) return;
    try {
      await expenseService.deleteSiteAdvance(advance.id);
      showToast('Advance deleted', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete advance', 'error');
    }
  };

  const handleEditExpense = async (expense) => {
    const amount = window.prompt('New amount (₹):', expense.amount);
    if (amount === null) return;
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      showToast('Enter a valid amount', 'error');
      return;
    }
    const description = window.prompt('Description:', expense.description || '');
    try {
      await expenseService.updateSiteExpense(expense.id, { amount: Number(amount), description: description || null });
      showToast('Expense updated', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update expense', 'error');
    }
  };

  const handleDeleteExpense = async (expense) => {
    if (!window.confirm('Delete this expense? This cannot be undone.')) return;
    try {
      await expenseService.deleteSiteExpense(expense.id);
      showToast('Expense deleted', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete expense', 'error');
    }
  };

  // Live advance pool balance for the selected engineer (2026-08-19: an
  // expense paid from advance draws from this pool, not one specific
  // advance row - see the batch-submit handler). Computed the same way
  // site_settlements does server-side: total advanced minus total
  // approved expenses. "After this batch" also nets out the lines in the
  // form still being drafted (unsaved), so a big batch that would overdraw
  // the pool is visible before submitting.
  const selectedEngineerBalance = settlements.find((s) => String(s.engineer_id) === String(expenseHeader.engineer_id))?.balance ?? null;
  const draftAdvanceTotal = expenseLines
    .filter((l) => l.paid_from === 'advance')
    .reduce((sum, l) => sum + (Number(l.amount) || 0), 0);

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <ExpenseTabs />
        <h1 className="text-2xl font-bold text-white mb-6">Site Expenses</h1>

        {/* Settlements */}
        <div className="glass-card p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-3">Settlements</h2>
          {settlements.length === 0 ? (
            <p className="text-sm text-gray-400">No advances or expenses recorded yet.</p>
          ) : (
            <ScrollX>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-3 py-2 font-medium">Engineer</th>
                    <th className="px-3 py-2 font-medium">Advanced</th>
                    <th className="px-3 py-2 font-medium">Expensed</th>
                    <th className="px-3 py-2 font-medium">Balance</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.map((s) => (
                    <tr key={s.engineer_id} className="border-b border-gray-50 last:border-0">
                      <td className="px-3 py-2">{s.engineer_name || s.engineer_id}</td>
                      <td className="px-3 py-2">{money(s.total_advanced)}</td>
                      <td className="px-3 py-2">{money(s.total_expensed)}</td>
                      <td className="px-3 py-2 font-medium">{money(s.balance)}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {s.action === 'engineer_owes' ? 'Engineer owes company' : s.action === 'company_owes' ? 'Company owes engineer' : 'Settled'}
                      </td>
                      <td className="px-3 py-2">
                        {s.action !== 'settled' && canApproveSiteManager && (
                          <button onClick={() => handleMarkSettled(s.engineer_id, s.engineer_name)} className="text-green-700 hover:underline text-xs font-semibold">Mark Settled</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </div>

        {/* Advances Given */}
        <div className="glass-card mb-6">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Advances Given ({advances.length})</h2>
          </div>
          {advances.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No advances given yet.</p>
          ) : (
            <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2 font-medium">Engineer</th>
                  <th className="px-4 py-2 font-medium">Project</th>
                  <th className="px-4 py-2 font-medium">Amount</th>
                  <th className="px-4 py-2 font-medium">Given Date</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {advances.map((a) => (
                  <tr key={a.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-3">{nameOf(employees, a.engineer_id)}</td>
                    <td className="px-4 py-3 text-gray-500">{nameOf(projects, a.project_id)}</td>
                    <td className="px-4 py-3 font-medium">{money(a.amount)}</td>
                    <td className="px-4 py-3">{a.given_date}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${a.status === 'Active' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                      {a.status === 'Active' && canGiveAdvance && (
                        <>
                          <button onClick={() => handleEditAdvance(a)} className="text-gray-600 hover:underline text-xs font-semibold">Edit</button>
                          <button onClick={() => handleDeleteAdvance(a)} className="text-red-500 hover:underline text-xs font-semibold">Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ScrollX>
          )}
        </div>

        {/* Advances */}
        {canGiveAdvance && (
          <div className="glass-card p-6 mb-6">
            <button onClick={() => setShowAdvanceForm(!showAdvanceForm)} className="text-sm font-semibold text-komal-blue hover:underline">
              {showAdvanceForm ? 'Hide' : '+ Give an advance'}
            </button>
            {showAdvanceForm && (
              <form onSubmit={handleAdvanceSubmit} className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Engineer *</label>
                  <select required value={advanceForm.engineer_id} onChange={(e) => setAdvanceForm({ ...advanceForm, engineer_id: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    <option value="">Select engineer</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project *</label>
                  <select required value={advanceForm.project_id} onChange={(e) => setAdvanceForm({ ...advanceForm, project_id: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    <option value="">Select project</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹) *</label>
                  <input required type="number" value={advanceForm.amount} onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Given date</label>
                  <input type="date" value={advanceForm.given_date} onChange={(e) => setAdvanceForm({ ...advanceForm, given_date: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <input value={advanceForm.notes} onChange={(e) => setAdvanceForm({ ...advanceForm, notes: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2">
                  <button type="submit" className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">Give Advance</button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Log expenses - multi-line: pick the engineer/project once, add
            as many rows as needed, submit together. Engineer/project stay
            filled in after submit so the next batch for the same person
            doesn't mean re-picking them. */}
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <button onClick={() => setShowExpenseForm(!showExpenseForm)} className="text-sm font-semibold text-komal-blue hover:underline">
              {showExpenseForm ? 'Hide' : '+ Log expenses'}
            </button>
            <div className="flex items-center gap-2">
              <button onClick={handleDownloadExpenseTemplate} className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
                Download Excel Template
              </button>
              <label className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 cursor-pointer">
                {isImporting ? 'Importing...' : 'Upload Excel'}
                <input type="file" accept=".xlsx,.xlsm" onChange={handleImportExpensesCsv} disabled={isImporting} className="hidden" />
              </label>
            </div>
          </div>
          {showExpenseForm && (
            <form onSubmit={handleExpenseBatchSubmit} className="mt-4 pt-4 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Engineer *</label>
                  <select required value={expenseHeader.engineer_id} onChange={(e) => setExpenseHeader({ ...expenseHeader, engineer_id: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    <option value="">Select engineer</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project *</label>
                  <select required value={expenseHeader.project_id} onChange={(e) => setExpenseHeader({ ...expenseHeader, project_id: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                    <option value="">Select project</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>

              {expenseHeader.engineer_id && selectedEngineerBalance !== null && (
                <p className="text-xs text-gray-500 mb-4">
                  Advance pool balance: <span className="font-medium text-gray-700">{money(selectedEngineerBalance)}</span>
                  {draftAdvanceTotal > 0 && (
                    <> — after this batch: <span className={`font-medium ${selectedEngineerBalance - draftAdvanceTotal < 0 ? 'text-red-600' : 'text-gray-700'}`}>{money(selectedEngineerBalance - draftAdvanceTotal)}</span></>
                  )}
                </p>
              )}

              <div className="space-y-3">
                {expenseLines.map((line, i) => (
                  <div key={line.key} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-500">Expense {i + 1}</span>
                      {expenseLines.length > 1 && (
                        <button type="button" onClick={() => removeExpenseLine(i)} className="text-red-500 hover:underline text-xs font-semibold">Remove</button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Category *</label>
                        <select value={line.category} onChange={(e) => updateExpenseLine(i, 'category', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm">
                          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Amount (₹) *</label>
                        <input required type="number" value={line.amount} onChange={(e) => updateExpenseLine(i, 'amount', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Date *</label>
                        <input required type="date" value={line.expense_date} onChange={(e) => updateExpenseLine(i, 'expense_date', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Paid from *</label>
                        <select value={line.paid_from} onChange={(e) => updateExpenseLine(i, 'paid_from', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm">
                          <option value="advance">Advance</option>
                          <option value="personal">Personal (reimbursement)</option>
                        </select>
                      </div>
                      <div className="col-span-2 md:col-span-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                        <input value={line.description} onChange={(e) => updateExpenseLine(i, 'description', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-3 mt-4">
                <button type="button" onClick={addExpenseLine} className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold">
                  + Add another line
                </button>
                <button type="submit" disabled={isSubmittingExpenses} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark disabled:opacity-50 text-white font-semibold">
                  {isSubmittingExpenses ? 'Submitting...' : `Submit ${expenseLines.length} Expense${expenseLines.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Filters - applied server-side (see loadAll) so this scales as
            the expenses/advances lists grow, rather than filtering an
            ever-larger client-side array. */}
        <div className="glass-card p-4 mb-6 flex flex-wrap gap-2 items-center">
          <select value={filters.engineer_id} onChange={(e) => setFilters({ ...filters, engineer_id: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All engineers</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <select value={filters.project_id} onChange={(e) => setFilters({ ...filters, project_id: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filters.approval_status} onChange={(e) => setFilters({ ...filters, approval_status: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="site_manager_approved">Site Manager Approved</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
          <span className="text-xs text-gray-400">to</span>
          <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
          <button onClick={clearFilters} className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 ml-auto">
            Clear Filters
          </button>
        </div>

        {/* Expenses table */}
        <div className="glass-card">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Expenses ({expenses.length})</h2>
          </div>
          {isLoading ? (
            <p className="text-gray-500 text-sm p-5">Loading...</p>
          ) : expenses.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No expenses match these filters.</p>
          ) : (
            <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2 font-medium">Engineer</th>
                  <th className="px-4 py-2 font-medium">Project</th>
                  <th className="px-4 py-2 font-medium">Category</th>
                  <th className="px-4 py-2 font-medium">Amount</th>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Paid From</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Approved By</th>
                  <th className="px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e) => (
                  <tr key={e.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-3">{nameOf(employees, e.engineer_id)}</td>
                    <td className="px-4 py-3 text-gray-500">{nameOf(projects, e.project_id)}</td>
                    <td className="px-4 py-3">{e.category}</td>
                    <td className="px-4 py-3 font-medium">{money(e.amount)}</td>
                    <td className="px-4 py-3">{e.expense_date}</td>
                    <td className="px-4 py-3 text-gray-500 capitalize">{e.paid_from}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${APPROVAL_COLORS[e.approval_status]}`}>
                        {APPROVAL_LABELS[e.approval_status]}
                      </span>
                      {e.approval_status === 'rejected' && e.rejection_reason && (
                        <div className="text-xs text-red-600 mt-1 max-w-[14rem]" title={e.rejection_reason}>{e.rejection_reason}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {e.admin_approved_by_name ? (
                        <>Final: {e.admin_approved_by_name}</>
                      ) : e.site_manager_approved_by_name ? (
                        <>1st tier: {e.site_manager_approved_by_name}</>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                      {e.approval_status === 'pending' && canApproveSiteManager && (
                        <button onClick={() => handleApproveSiteManager(e.id)} className="text-komal-blue hover:underline text-xs font-semibold">Approve (1st)</button>
                      )}
                      {e.approval_status === 'site_manager_approved' && canApproveAdmin && (
                        <button onClick={() => handleApproveAdmin(e.id)} className="text-green-700 hover:underline text-xs font-semibold">Approve (Final)</button>
                      )}
                      {(e.approval_status === 'pending' || e.approval_status === 'site_manager_approved') && canApproveSiteManager && (
                        <button onClick={() => handleReject(e.id)} className="text-red-500 hover:underline text-xs font-semibold">Reject</button>
                      )}
                      {e.approval_status === 'pending' && (
                        <>
                          <button onClick={() => handleEditExpense(e)} className="text-gray-600 hover:underline text-xs font-semibold">Edit</button>
                          <button onClick={() => handleDeleteExpense(e)} className="text-red-500 hover:underline text-xs font-semibold">Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ScrollX>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteExpensesPage;
